import React, { Component } from "react";
import { Link } from "react-router-dom";
import CESSVG from "../../Assets/svg/CESSVG.svg";
import LoginImage from "../../Assets/jpeg/LoginOverLay.jpg";
import {
  LoginAction,
  FlushAuthenticationAction,
} from "../../Actions/Authentication";
import { ResetAnalysisReduxStateAction } from "../../Actions/Analysis";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouterHooks } from "../../withRouters/withRoutersHook";
import { Loader2, Eye, EyeOff, AlertCircle, ArrowRight } from "lucide-react";
import AuthenticationMessageDispatcher from "./AuthenticationMessageDispatcher";

class Login extends Component {
  state = {
    email: "",
    password: "",
    loggingIn: false,
    showErrorMessage: false,
    showPassword: false,
  };

  static propTypes = {
    LoginAction: PropTypes.func.isRequired,
    ResetAnalysisReduxStateAction: PropTypes.func.isRequired,
    FlushAuthenticationAction: PropTypes.func.isRequired,
    isAuthenticated: PropTypes.bool,
    navigate: PropTypes.func.isRequired,
    authenticationNotSuccessful: PropTypes.bool,
  };

  timeoutId = null;

  componentDidMount() {
    this.props.FlushAuthenticationAction();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.isAuthenticated &&
      this.props.isAuthenticated !== prevProps.isAuthenticated
    ) {
      this.setState({ loggingIn: true });
      this.timeoutId = setTimeout(() => {
        if (this.handlePath().path === "/login") {
          this.props.navigate("/dashboard");
        } else if (this.handlePath().path === "/admin-login") {
          this.props.navigate("/admin-dashboard");
        }
      }, 3000);
    }
    if (
      this.props.authenticationNotSuccessful &&
      this.props.authenticationNotSuccessful !== prevProps.authenticationNotSuccessful
    ) {
      this.setState({ showErrorMessage: true });
      this.timeoutId = setTimeout(() => {
        this.setState({ showErrorMessage: false });
        this.props.FlushAuthenticationAction();
      }, 2000);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutId);
  }

  onSubmit = (e) => {
    e.preventDefault();
    this.props.LoginAction(this.state.email, this.state.password);
    this.props.ResetAnalysisReduxStateAction();
  };

  onChange = (e) => this.setState({ [e.target.name]: e.target.value });

  handlePath = () => {
    let pathVar = { text: "", path: "" };
    if (this.props.location.pathname === "/login") {
      pathVar.text = "Sign in";
      pathVar.path = "/login";
    } else if (this.props.location.pathname === "/admin-login") {
      pathVar.text = "Admin Portal";
      pathVar.path = "/admin-login";
    }
    return pathVar;
  };

  toggleShowPassword = () => {
    this.setState((prevState) => ({ showPassword: !prevState.showPassword }));
  };

  render() {
    const { email, password, showPassword, loggingIn, showErrorMessage } = this.state;
    const { text, path } = this.handlePath();
    const isAdmin = path === "/admin-login";

    return (
      <div className="flex w-full min-h-screen bg-white">

        {/* ── Left panel ── */}
        <div className="flex flex-col flex-1 min-h-screen px-8 py-10 lg:px-16">

          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <img src={CESSVG} alt="Logo" className="w-8 h-8" />
            <span className="font-SFPro font-bold text-gray-900 text-lg tracking-tight">CX2 AI</span>
          </div>

          {/* Form — vertically centred */}
          <div className="flex flex-col flex-1 justify-center">
            <div className="w-full max-w-[380px] mx-auto">

              {/* Badge for admin */}
              {isAdmin && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-6 rounded-full bg-amber-50 border border-amber-200 text-xs font-inter font-semibold text-amber-700 uppercase tracking-wider">
                  Admin access
                </span>
              )}

              {/* Heading */}
              <h1 className="font-SFPro font-bold text-[2rem] leading-tight text-gray-900 mb-2">
                {text}
              </h1>
              <p className="font-inter text-sm text-gray-400 mb-8">
                Enter your credentials to access your workspace.
              </p>

              {/* Error */}
              {showErrorMessage && (
                <div className="flex items-start gap-2.5 px-4 py-3 mb-6 bg-red-50 border border-red-100 rounded-xl text-sm font-inter text-red-600">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <AuthenticationMessageDispatcher />
                </div>
              )}

              <form onSubmit={this.onSubmit} className="flex flex-col gap-4">

                {/* Email */}
                <div className="flex flex-col gap-1.5">
                  <label className="font-inter font-medium text-sm text-gray-700">
                    Email address
                  </label>
                  <input
                    type="text"
                    name="email"
                    value={email}
                    onChange={this.onChange}
                    placeholder="you@company.com"
                    autoComplete="on"
                    className="w-full h-11 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm font-inter text-gray-900 placeholder-gray-400 outline-none focus:bg-white focus:border-primary-blue transition-all"
                  />
                </div>

                {/* Password */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="font-inter font-medium text-sm text-gray-700">
                      Password
                    </label>
                    {path === "/login" && (
                      <Link
                        to="/forgotpassword"
                        className="font-inter text-xs text-primary-blue hover:underline"
                      >
                        Forgot password?
                      </Link>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={password}
                      onChange={this.onChange}
                      placeholder="••••••••"
                      autoComplete="on"
                      className="w-full h-11 px-4 pr-11 bg-gray-50 border border-gray-200 rounded-xl text-sm font-inter text-gray-900 placeholder-gray-400 outline-none focus:bg-white focus:border-primary-blue transition-all"
                    />
                    <button
                      type="button"
                      onClick={this.toggleShowPassword}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword
                        ? <EyeOff className="w-4 h-4" />
                        : <Eye className="w-4 h-4" />
                      }
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loggingIn || !email || !password}
                  className="mt-2 flex items-center justify-center gap-2 w-full h-11 bg-primary-blue text-white font-inter font-semibold text-sm rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  {loggingIn ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      Sign in
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              {/* Sign up */}
              {path === "/login" && (
                <p className="mt-6 text-center font-inter text-sm text-gray-400">
                  Don't have an account?{" "}
                  <Link to="/signup" className="text-primary-blue font-semibold hover:underline">
                    Create one
                  </Link>
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <p className="font-inter text-xs text-gray-300 text-center">
            © {new Date().getFullYear()} CX2 AI. All rights reserved.
          </p>
        </div>

        {/* ── Right panel — image ── */}
        <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden sticky top-0 h-screen">
          <img
            src={LoginImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D6EFD]/60 to-gray-900/70" />

          {/* Floating card on the image */}
          <div className="relative z-10 flex flex-col justify-end h-full p-12">
            <div className="backdrop-blur-sm bg-white/10 border border-white/20 rounded-2xl p-6 max-w-sm">
              <p className="font-SFPro font-semibold text-white text-lg leading-snug mb-3">
                "CX2 AI has transformed how we handle customer conversations at scale."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">
                  CX
                </div>
                <div>
                  <p className="font-inter font-medium text-white text-sm">Customer Experience Team</p>
                  <p className="font-inter text-white/60 text-xs">Enterprise</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  isAuthenticated: state.authentication?.isAuthenticated,
  authenticationNotSuccessful: state.authentication?.authenticationNotSuccessful,
});

export default withRouterHooks(
  connect(mapStateToProps, {
    LoginAction,
    FlushAuthenticationAction,
    ResetAnalysisReduxStateAction,
  })(Login)
);
